import {
  useEffect,
  useRef,
  useState,
  KeyboardEvent,
  MouseEvent,
  useCallback,
} from "react";
import { useFirstRender } from "@utils/hooks/useFirstRender";
import { MinerIdFieldPropsI } from "./MinerIdField.props";
import { makeNotifs } from "@utils/makeNotifs";
import { useMe } from "@utils/hooks/useMe";
import { minerNameToLocation } from "@utils/minerNameToLocation";
import useApiRequests from "@services/http/useApiRequest";
import { SetMinerLocationByName } from "@services/rackService";
import { AdminChangeSettingsRequest, moveMinerToSpecialTabRequest } from "@services/changeMinerSettings";
import { useDispatch } from "react-redux";
import { addMinerChangeLogs } from "@store/minerChangeLogs/actions";
import { show } from "redux-modal";
import { MODALS_ENUM } from "@constants/Modals";
import { useIntl } from "react-intl";
import { useTransliting } from "@utils/translitingText";
import { RecordResponse } from "@services/http/responses/BaseResponse";
import { GetMinerNames, GetMinerNamesResponse } from "@services/allMinerNames";
import { isCurrentStr } from "@utils/isRussianLang";
import { useToasts } from "@utils/hooks/useToasts";
import { useMinerContext } from "../MinerContext";
import { MinerActions } from "../minerReducer";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import useTypedRequest from "@utils/hooks/useTypedRequest";
import { Tab } from "@store/minersSelect/actionTypes";


export const useMinerIdFieldControl = (props: MinerIdFieldPropsI) => {
  const { text } = props;
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const allMinerNames = useRef<Set<string> | null>();
  const { formatMessage } = useIntl();
  const { miner, dispatchMiner } = useMinerContext();
  const dispatch = useDispatch();
  const firstRender = useFirstRender();
  const { ok, errors: notifsErrors } = makeNotifs();
  const { onSuccess, onLoader } = useGlobalNotifs();
  const { error } = useToasts();
  const me = useMe();
  const nameCheckRequestRef = useRef(0);
  const { enqueue } = useApiRequests();
  const { onTranslit } = useTransliting();
  const [oldValue, setOldValue] = useState<string>(text || "");
  const [newId, setNewId] = useState<string>(text || "");
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [hasEqualName, setHasEqualName] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { request: moveMinerToSpecialTab } = useTypedRequest(
    moveMinerToSpecialTabRequest
  );

  useEffect(() => {
    onLoader(loading);
  }, [loading]);

  useEffect(() => {
    const requestId = ++nameCheckRequestRef.current;

    if (newId === oldValue) {
      setHasEqualName(false);
      return;
    }

    const checkedValue = newId;
    const t = setTimeout(() => {
      checkMinerName(checkedValue, requestId);
    }, 100);
    return () => clearTimeout(t);
  }, [newId, oldValue]);

  const checkMinerName = async (value: string, requestId = ++nameCheckRequestRef.current) => {
    const emptyNames = allMinerNames.current?.size === 0;
    if (!allMinerNames.current || emptyNames) {
      const res = await enqueue<GetMinerNamesResponse>(new GetMinerNames());
      if (!res) return;
      if (nameCheckRequestRef.current !== requestId) return;
      const s = new Set(res.Record);
      allMinerNames.current = s;
    }
    if (nameCheckRequestRef.current !== requestId) return;

    const hasEqualName = allMinerNames.current.has(value);
    setHasEqualName(hasEqualName);
    return hasEqualName;
  };

  useEffect(() => {
    if (firstRender) return;

    setNewId(text);
    setOldValue(text);
  }, [text]);

  const onBlur = () => {
    setIsEdit(false);
    textAreaRef.current?.blur();
    wrapperRef.current?.blur();
  };

  const cancelChanges = useCallback(() => {
    setNewId(oldValue);
    onBlur();
    setHasEqualName(false);
  }, [oldValue]);

  useEffect(() => {
    const onCancel = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape" && isEdit) {
        cancelChanges();
      }
    };
    document.addEventListener("keydown", onCancel);
    return () => {
      document.removeEventListener("keydown", onCancel);
    };
  }, [cancelChanges, isEdit]);

  const formatMinerLocation = (name: string): boolean => {
    const address = name.split("-");
    if (address.length < 3) return false;
    return true;
  };

  const restore = async () => {
    dispatch(
      show(MODALS_ENUM.CONFIRM_MODAL, {
        subtitle: formatMessage({ id: "machines.miner_row.miner_id.restore" }) + "?",
        async onConfirm() {
          const id = miner.miner_id.split("_")[0].replace(/(K\d{2})/, "-$1-");
          setNewId(id);
          await onSave(id);
          await moveMinerToSpecialTab(Tab.RACKS, [String(miner.id)], true);
        },
      })
    );
  };

  const onSave = async (value = newId) => {
    if (value === oldValue) {
      cancelChanges();
      return;
    }
    let minerId = onTranslit(value);

    if (!isCurrentStr(minerId)) {
      error(notifsErrors.symbols);
      cancelChanges();
      return;
    }
    setLoading(true);
    const hasDuplicate = await checkMinerName(minerId);
    if (hasDuplicate) {
      setLoading(false);
      return;
    }
    if (
      formatMinerLocation(minerId) &&
      !!minerNameToLocation(minerId).x &&
      !!minerNameToLocation(minerId).y
    ) {
      const values = minerNameToLocation(minerId);
      if (!!values.letter) {
        minerId = `${values.location}${values.object}${values.rack_id}${values.letter || "A"
          }${values.y}${values.x}`;

        const res = await enqueue<RecordResponse<string>>(
          new SetMinerLocationByName({
            miner_id: miner.id.toString(),
            location: values.location,
            object: values.object,
            rack_id: (+values.rack_id).toString(),
            letter: values.letter,
            x: values.x,
            y: values.y,
          })
        );
        if (!res) {
          setNewId(text);
          setLoading(false);
          return;
        }
        minerId = res.Record;
      }
    } else {
      const res = await enqueue(
        new AdminChangeSettingsRequest({
          miner: miner.id.toString(),
          miner_id: minerId,
        })
      );
      if (!res) return setLoading(false);
    }
    if (oldValue !== minerId) {
      dispatchMiner({
        type: MinerActions.MinerId,
        payload: { id: "", created: Date.now(), miner_id: minerId },
      });
    }
    setNewId(minerId);
    setOldValue(minerId);

    dispatch(
      addMinerChangeLogs({
        id: miner.id,
        prev: props.text,
        next: minerId,
      })
    );
    setLoading(false);
    onBlur();
    onSuccess(ok.success);
  };

  const onClickHandler = (e: MouseEvent<HTMLDivElement>) => {
    onSave();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Escape") {
      cancelChanges();
    }
    if (e.key === "Enter") {
      onSave();
    }
  };

  const onCancel = (e: MouseEvent<HTMLDivElement>) => {
    cancelChanges();
    onBlur();
  };

  const onFocus = () => {
    setIsEdit(true);
    setTimeout(() => textAreaRef.current?.focus(), 1);
  };

  useEffect(() => {
    if (isEdit || firstRender) return;

    const t = setTimeout(() => {
      if (loading || !newId) {
        cancelChanges();
        onBlur();
        return;
      }
      if (oldValue !== newId && !hasEqualName) {
        dispatch(
          show(MODALS_ENUM.CONFIRM_MODAL, {
            title: formatMessage(
              {
                id: "machines.miner_row.miner_id.confirm_change",
              },
              { newId }
            ),
            onConfirm: onSave,
            onCancel: onCancel,
          })
        );
      }
      if (hasEqualName) {
        cancelChanges();
        onBlur();
      }
    }, 100);

    return () => clearTimeout(t);
  }, [isEdit]);

  return {
    me,
    isEdit,
    textAreaRef,
    wrapperRef,
    newId,
    oldValue,
    hasEqualName,
    miner,

    onClickHandler,
    onKeyDown,
    onCancel,
    setNewId,
    onFocus,
    onBlur,
    restore,
  };
};
